import { useCallback, useEffect, useRef } from "react";

/**
 * How long a link has to be hovered before its page's data is fetched. Long
 * enough that a pointer crossing a list of links on its way somewhere else
 * doesn't fetch every one it passes over, short enough to still be reading
 * ahead of the click.
 */
const INTENT_DELAY_MS = 120;

/** Handlers to spread onto the element standing in for the link. */
export interface PreloadIntentHandlers {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * Calls `onPreload` once the user looks like they are heading for a link —
 * hovering it for a moment, or tabbing onto it.
 *
 * The handlers are inert when there is nothing to preload, so callers can
 * spread them unconditionally.
 */
export function usePreloadIntent(onPreload: (() => void) | undefined): PreloadIntentHandlers {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const cancel = useCallback(() => clearTimeout(timer.current), []);

  // Also the unmount cleanup: cancelling is all there is to clean up.
  useEffect(() => cancel, [cancel]);

  const start = () => {
    if (!onPreload) {
      return;
    }
    cancel();
    timer.current = setTimeout(onPreload, INTENT_DELAY_MS);
  };

  return {
    onMouseEnter: start,
    onMouseLeave: cancel,
    onFocus: start,
    onBlur: cancel,
  };
}
